// 2026-07-13 renamed from ConversionBreakdown.js. Touched to invalidate a
// stale webpack/ESLint cache — the file was reported with a phantom parse
// error at line 57 while the sibling module imports were broken; the file
// itself parses cleanly under both @babel/parser and espree.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber } from '../../shared/format';
import { ExportMenu, downloadCsv, downloadPdf } from '../shared';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonText } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';

const TIER_STYLES = [
  { headerBg: 'bg-emerald-50 dark:bg-emerald-900/20', headerBorder: 'border-emerald-200 dark:border-emerald-800', headerText: 'text-emerald-800 dark:text-emerald-300', barColor: 'bg-emerald-500' },
  { headerBg: 'bg-blue-50 dark:bg-blue-900/20', headerBorder: 'border-blue-200 dark:border-blue-800', headerText: 'text-blue-800 dark:text-blue-300', barColor: 'bg-blue-500' },
  { headerBg: 'bg-gray-50 dark:bg-gray-800/50', headerBorder: 'border-gray-200 dark:border-gray-700', headerText: 'text-gray-700 dark:text-gray-300', barColor: 'bg-gray-400' },
];

const ConversionBreakdown = ({ convBreakdown, loading, error, rangeStart, rangeEnd }) => {
  const [expanded, setExpanded] = useState({ 0: true, 1: true, 2: true });
  const [barTip, setBarTip] = useState(null);
  const barTipRef = useRef(null);

  // Keep the segmented-bar hover tooltip fully inside the viewport — centre it
  // on the cursor, clamp horizontally, and flip below the cursor if it would
  // clip the top edge.
  useLayoutEffect(() => {
    if (!barTip || !barTipRef.current) return;
    const el = barTipRef.current;
    const { width, height } = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const left = Math.max(8, Math.min(barTip.x - width / 2, vw - width - 8));
    let top = barTip.y - height - 12;
    if (top < 8) top = barTip.y + 16;
    top = Math.max(8, Math.min(top, vh - height - 8));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [barTip]);

  // 2026-07-13 — RPC returns tier rows that may lack an `items` array (empty
  // periods, migration gaps). Coerce to [] everywhere to avoid `.map of undefined`.
  const itemsOf = (tier) => (Array.isArray(tier?.items) ? tier.items : []);

  // Review A2 (2026-07-23) — `summary.tier2_leads` is DB-computed to EXCLUDE
  // PHONE_CALL_LEAD (it only counted CONTACT/SUBMIT_LEAD_FORM etc.), so it
  // under-reports leads (27 vs ~162). Per spec, Tier 2 leads = PHONE_CALL_LEAD
  // + SUBMIT_LEAD_FORM + CONTACT + BOOK_APPOINTMENT. The Tier 2 tier's
  // `total_conversions` already sums all four categories (built in
  // getGoogleAdsConversionsBreakdown), so derive the figure from there and fall
  // back to the summary field only if the tier row is missing. Phone Calls is a
  // sub-slice of Tier 2, not a separate bucket, so this does not double-count.
  const tier2Row = (convBreakdown?.tiers || []).find((t) => /tier 2/i.test(t?.tier || ''));
  const tier2Leads = tier2Row
    ? tier2Row.total_conversions
    : convBreakdown?.summary?.tier2_leads;

  const exportCsv = () => {
    if (!convBreakdown?.tiers?.length) return;
    const headers = ['Tier', 'Action', 'Type', 'Conversions', 'Value'];
    const rows = convBreakdown.tiers.flatMap(tier =>
      itemsOf(tier).map(a => [tier.tier, a.conversion_action, a.conversion_type || '', a.conversions, a.conversion_value || 0])
    );
    downloadCsv(headers, rows, `conversion-actions-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!convBreakdown?.tiers?.length) return;
    const head = ['Tier', 'Action', 'Type', 'Conversions', 'Value'];
    const body = convBreakdown.tiers.flatMap(tier =>
      itemsOf(tier).map(a => [tier.tier, a.conversion_action, a.conversion_type || '', fmtNumber(a.conversions, true), fmtCurrency(a.conversion_value)])
    );
    downloadPdf('Conversion Actions Breakdown', head, body, `conversion-actions-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5 relative">
      {convBreakdown?.tiers?.length > 0 && (
        <div className="absolute top-4 right-4"><ExportMenu onCsv={exportCsv} onPdf={exportPdf} /></div>
      )}
      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 pr-10 inline-flex items-center">
        Conversion Actions Breakdown
        <InfoTip text="Conversions broken out by the action people took — bookings, phone calls, form fills, pageviews, and other goals — grouped from highest-value to lowest." />
      </h2>

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <SkeletonText lines={6} lineClass="h-10" gapClass="space-y-3" />
      ) : !convBreakdown?.tiers?.length ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No conversion data available for this period.</p>
      ) : (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmtNumber(convBreakdown.summary.tier1_bookings, true)} bookings</span>
            <span className="text-gray-400"> ({fmtCurrency(convBreakdown.summary.tier1_value)} value)</span>
            <span className="mx-1.5 text-gray-300 dark:text-gray-600">&middot;</span>
            <span className="font-semibold text-blue-600 dark:text-blue-400">{fmtNumber(tier2Leads, true)} leads</span>
            <span className="mx-1.5 text-gray-300 dark:text-gray-600">&middot;</span>
            <span className="font-semibold text-gray-500 dark:text-gray-400">{fmtNumber(convBreakdown.summary.tier3_engagements, true)} engagement events</span>
          </p>

          <div className="space-y-3">
            {convBreakdown.tiers.map((tier, tierIdx) => {
              const s = TIER_STYLES[tierIdx] || TIER_STYLES[2];
              const isExpanded = expanded[tierIdx] ?? false;
              const items = itemsOf(tier);
              const maxConv = items.length ? Math.max(...items.map((a) => a.conversions), 1) : 1;

              return (
                <div key={tier.tier} className={`rounded-lg border ${s.headerBorder} overflow-hidden`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [tierIdx]: !prev[tierIdx] }))}
                    className={`w-full flex items-center justify-between px-4 py-3 ${s.headerBg} hover:opacity-90 transition-opacity`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${s.headerText}`}>{tier.tier}</span>
                      <span className={`text-xs ${s.headerText} opacity-70`}>
                        {fmtNumber(tier.total_conversions, true)} conversions &middot; {fmtCurrency(tier.total_value)}
                      </span>
                    </div>
                    <svg className={`w-4 h-4 ${s.headerText} transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="px-4 py-3">
                      <div className="flex rounded-full overflow-hidden h-3 mb-4 bg-gray-100 dark:bg-gray-800">
                        {items.map((action, i) => {
                          const widthPct = tier.total_conversions > 0 ? (action.conversions / tier.total_conversions) * 100 : 0;
                          return (
                            <div
                              key={action.conversion_action}
                              className="transition-all duration-300 relative"
                              style={{
                                width: `${Math.max(widthPct, 1)}%`,
                                opacity: i === 0 ? 1 : Math.max(0.35, 1 - i * 0.2),
                              }}
                              onMouseEnter={(e) => setBarTip({ x: e.clientX, y: e.clientY, action: action.conversion_action, conversions: action.conversions })}
                              onMouseMove={(e) => setBarTip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null))}
                              onMouseLeave={() => setBarTip(null)}
                            >
                              <div className={`h-full w-full ${s.barColor}`} style={{ opacity: Math.max(0.3, 1 - i * 0.18) }} />
                            </div>
                          );
                        })}
                      </div>

                      <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto">
                      <table className="responsive-card-table-650 w-full text-xs table-fixed min-w-[480px]">
                        <colgroup>
                          <col style={{ width: '38%' }} />
                          <col style={{ width: '17%' }} />
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead>
                          <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                            <th className="text-left py-2 font-medium">Action</th>
                            <th className="text-left py-2 font-medium">Type</th>
                            <th className="text-right py-2 font-medium">Conversions</th>
                            <th className="text-right py-2 font-medium">Value</th>
                            <th className="text-right py-2 font-medium">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((action) => (
                            <tr key={action.conversion_action} className="border-b dark:border-gray-800 last:border-0">
                              <td data-label="Action" className="py-2.5 text-gray-900 dark:text-gray-100 font-medium">{action.conversion_action}</td>
                              <td data-label="Type" className="py-2.5">
                                <span className="inline-block px-2 py-0.5 rounded text-[12px] font-semibold uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                  {(action.conversion_type || '').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td data-label="Conversions" className="text-right text-gray-700 dark:text-gray-300">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                                    <div className={`h-full rounded-full ${s.barColor}`} style={{ width: `${(action.conversions / maxConv) * 100}%` }} />
                                  </div>
                                  {fmtNumber(action.conversions, true)}
                                </div>
                              </td>
                              <td data-label="Value" className="text-right text-gray-700 dark:text-gray-300">{fmtCurrency(action.conversion_value)}</td>
                              <td data-label="Share" className="text-right text-gray-700 dark:text-gray-300">
                                {tier.total_conversions > 0
                                  ? `${((action.conversions / tier.total_conversions) * 100).toFixed(1)}%`
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </ThinScroll>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {barTip && createPortal(
            <div
              ref={barTipRef}
              className="fixed z-[9999] pointer-events-none bg-gray-900 text-white text-sm rounded-lg px-3 py-2 shadow-xl max-w-[14rem]"
              style={{ left: barTip.x, top: barTip.y }}
            >
              <div className="font-semibold">{barTip.action}</div>
              <div>{fmtNumber(barTip.conversions, true)} conversions</div>
            </div>,
            document.body,
          )}
        </>
      )}
    </div>
  );
};

export default ConversionBreakdown;
